import { callFirebaseFunction, storage } from 'boot/firebase';
import { i18n } from 'boot/i18n';
import { documentId, serverTimestamp } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { chain, chunk, cloneDeep, findIndex, flatten, get, isEmpty, isNull, omit, set } from 'lodash';
import DataDB from 'src/fb/data-db';
import ProgramFilesDB from 'src/fb/programFiles-db';
import ProgramsDB from 'src/fb/programs-db';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import { auth as useAuthStore } from 'src/store/auth';
import { firebaseUploader as useFirebaseUploaderStore } from 'src/store/firebaseUploader';
import { bindDataBaseStore, unbindDataBaseStore } from 'src/utils/FirestoreUtils';
import { logEvent } from 'src/utils/LogEvent';
import { safeDeleteObject } from 'src/utils/StorageUtils';

export default {

  setPrograms(programs) {
    this.programs = programs;
  }, // setPrograms()

  setIsLoadingPrograms(isLoadingPrograms) {
    this.isLoadingPrograms = isLoadingPrograms;
  }, // setIsLoadingPrograms()

  setCachedGroupPrograms(programs) {
    const programsPerClient = {};

    programs?.forEach?.((program) => {
      if (programsPerClient[program.client]) {
        programsPerClient[program.client].push(program);
      } else {
        programsPerClient[program.client] = [program];
      }
    });

    this.cachedGroupPrograms = { ...this.cachedGroupPrograms, ...programsPerClient };
  }, // setCachedGroupPrograms()

  setIsLoadingProgramTemplates(isLoadingProgramTemplates) {
    this.isLoadingProgramTemplates = isLoadingProgramTemplates;
  }, // setIsLoadingProgramTemplates()

  setProgramTemplatesLoaded(programTemplatesLoaded) {
    this.programTemplatesLoaded = programTemplatesLoaded;
  }, // setProgramTemplatesLoaded()

  setProgramTemplates(programTemplates) {
    this.programTemplates = programTemplates;
  }, // setProgramTemplates()

  setProgramTargetMutation({ programIndex, programTargets }) {
    this.programs[programIndex].targets = programTargets;
  }, // setProgramTargetMutation()

  setCachedGroupProgramTarget({ clientId, groupProgramIndex, programTargets }) {
    this.cachedGroupPrograms[clientId][groupProgramIndex].targets = programTargets;
  }, // setCachedGroupProgramTarget()

  setDeletedPrograms(programs = []) {
    this.deletedPrograms = programs;
  }, // setDeletedPrograms()

  /**
   * Fetch all programs related to current client
   */
  async bindPrograms() {
    const auth = useAuthStore();

    if (isEmpty(auth.currentClient)) {
      return null;
    }
    this.setIsLoadingPrograms(true);

    const programsDb = new ProgramsDB();
    const query = programsDb.getQuery(
      [
        ['client', '==', auth.currentClient.id],
        ['isDeleted', '==', false],
      ],
      'programName',
      'asc',
    );

    const bindRef = await bindDataBaseStore({ store: this, property: 'programs' }, query, []);
    this.setIsLoadingPrograms(false);
    return bindRef;
  }, // bindPrograms()

  unbindPrograms() {
    return unbindDataBaseStore({ store: this, property: 'programs' }, []);
  }, // unbindPrograms()

  async bindGroupPrograms({ groupSessionClientIds }) {
    const auth = useAuthStore();

    const bindIds = groupSessionClientIds.filter((clientId) => {
      return clientId !== auth.currentClient.id && !this.cachedGroupPrograms?.[clientId];
    });

    if (!bindIds.length) {
      return;
    }

    const programsDb = new ProgramsDB();
    const query = programsDb.getQuery(
      [
        ['client', 'in', bindIds],
        ['isDeleted', '==', false],
      ],
    );
    return bindDataBaseStore({
      store: this,
      property: 'cachedGroupPrograms',
      action: 'setCachedGroupPrograms',
    }, query, {});
  }, // bindGroupPrograms()

  unbindGroupPrograms() {
    unbindDataBaseStore({
      store: this,
      property: 'cachedGroupPrograms',
      action: 'setCachedGroupPrograms',
    }, {});
  }, // unbindGroupPrograms

  /**
   * Fetch all programs related to current client
   */
  async bindProgramTemplates() {
    if (this.programTemplatesLoaded) {
      return null;
    }

    this.setIsLoadingProgramTemplates(true);
    this.setProgramTemplatesLoaded(true);

    const programsDb = new ProgramsDB();

    const auth = useAuthStore();

    const query = programsDb.getQuery([
      ['clinic', '==', auth.userDetails.clinic],
      ['client', '==', null],
      ['parentFolder', '==', 'templates'],
      ['isDeleted', '==', false],
    ]);

    const bindRef = await bindDataBaseStore({ store: this, property: 'programTemplates' }, query, []);
    this.setIsLoadingProgramTemplates(false);
    return bindRef;
  }, // bindProgramTemplates()

  unbindProgramTemplates() {
    this.setIsLoadingProgramTemplates(false);
    this.setProgramTemplatesLoaded(false);

    return unbindDataBaseStore({ store: this, property: 'programTemplates' }, []);
  }, // unbindProgramTemplates()

  /**
   * Create a program
   */
  async createProgram({ saveProgram: program }) {
    const programsDb = new ProgramsDB();
    const newProgram = await programsDb.create({
      ...omit(program, ['progressSettings', 'typesenseDataHash']),
      isDeleted: false,
      isLocked: false,
      filesRemoved: false, // were files hard-deleted after grace-period of 14 days
    });

    if (program.progressSettings) {
      // Progress settings may reference program id, so we need to update it after program creation
      const progressSettings = cloneDeep(program.progressSettings);
      if (!chain(program)
        .get(['progressSettings', 'dataSeriesSettings', 'phaseLines'])
        .isEmpty()
        .value()) {
        set(
          progressSettings,
          ['dataSeriesSettings', 'phaseLines'],
          chain(program)
            .get(['progressSettings', 'dataSeriesSettings', 'phaseLines'])
            .map((phaseLine) => ({
              ...phaseLine,
              ...(phaseLine.program === null ? { program: newProgram.id } : {}),
            }))
            .value(),
        );
      }
      if (!chain(program)
        .get(['progressSettings', 'dataSeriesSettings', 'postloadFilter', 'programs', 'programId'])
        .isEmpty()
        .value()) {
        set(
          progressSettings,
          ['dataSeriesSettings', 'postloadFilter', 'programs', 'programId'],
          chain(program)
            .get(['progressSettings', 'dataSeriesSettings', 'postloadFilter', 'programs', 'programId'])
            .map((filterProgramId) => (filterProgramId === null ? newProgram.id : filterProgramId))
            .value(),
        );
      }
      await programsDb.set({ progressSettings }, newProgram.id);
    }

    if (isEmpty(program.client) && !isEmpty(program.clinic)) {
      try {
        await this.syncLibraryPrograms({ programs: [newProgram], changeType: 'CREATE' });
      } catch (err) {
        console.error(`Created program Typesense sync error: ${err}`);
        logEvent(err);
      }
    }

    return newProgram;
  }, // createProgram()

  /**
   * Update info about a program
   */
  async updateProgram({ saveProgram: program, isProgramFromLibrary = false }) {
    const programsDb = new ProgramsDB();
    // Strip typesenseDataHash for non-library (client-created) programs
    const programToSave = isProgramFromLibrary ? program : omit(program, ['typesenseDataHash']);
    await programsDb.update(programToSave);

    if (isProgramFromLibrary) {
      try {
        await this.syncLibraryPrograms({ programs: [programToSave], changeType: 'UPDATE' });
      } catch (err) {
        console.error(`Updated program Typesense sync error: ${err}`);
        logEvent(err);
      }
    }
  }, // updateProgram()

  /**
   * Delete a program
   */
  async deleteProgram({ program, getFromDb = false }) {
    const programsDb = new ProgramsDB();

    const firebaseUploader = useFirebaseUploaderStore();
    if (firebaseUploader.uploadQueueContains(`programs/${program.id}/demo/`)) {
      // @Todo Throw an error
      throw new Error(i18n.t('store.deleteProgramError'));
    }

    const deleteProgram = getFromDb
      ? await programsDb.read(program.id)
      : { ...program, id: program.id };
    const isProgramFromLibrary = !get(deleteProgram, 'client') && !!get(deleteProgram, 'clinic');

    const programTargetsDb = new ProgramTargetsDB(program.id);
    const programFilesDb = new ProgramFilesDB(program.id);
    const dataDb = new DataDB();

    const [programTargets, programFiles] = await Promise.all([
      programTargetsDb.readAll(null, null, null, 1),
      programFilesDb.readAll(null, null, null, 1),
    ]);
    let dataDocs = [];
    if (!isProgramFromLibrary) {
      dataDocs = await dataDb.readAll([
        ['client', '==', deleteProgram.client],
        ['program', '==', deleteProgram.id],
      ], null, null, 1);
    }

    const isPackageProgram = !isNull(deleteProgram.package);

    // hard-delete a program when it has no targets, files and data documents and belongs to package
    if (isPackageProgram || (isEmpty(programTargets) && isEmpty(programFiles) && isEmpty(dataDocs))) {
      try {
        // Delete attached demo videos
        await this.deleteProgramFiles(deleteProgram);
      } catch (err) {
        logEvent(err);
      } finally {
        await programsDb.deleteProgram(deleteProgram.id);
      }

      if (isProgramFromLibrary) {
        try {
          await this.syncLibraryPrograms({ programs: [deleteProgram], changeType: 'DELETE' });
        } catch (err) {
          console.error(`Hard deleted program Typesense sync error: ${err}`);
          logEvent(err);
        }
      }

      return;
    }

    // soft-delete program
    return this.updateProgram({
      saveProgram: {
        id: program.id,
        isDeleted: true,
        deleteTimestamp: serverTimestamp(),
      },
      isProgramFromLibrary,
    });
  }, // deleteProgram

  async syncLibraryPrograms({ programs, changeType }) {
    await callFirebaseFunction('syncLibraryPrograms', { programs, changeType });
  }, // syncLibraryPrograms()

  /**
   * Delete a file (e.g. demo video) from a program
   */
  async deleteProgramFile({ program, programFile }) {
    // Check if others are using the same file
    const programFileDb = new ProgramFilesDB(program.id);

    // old files without transcoding
    if (programFile.src) {
      const allProgramFiles = await programFileDb.findFileByField('src', programFile.src, 2);

      if (allProgramFiles.length <= 1) {
        // When programs with demo videos are duplicated we don't physically duplicate
        // the file. Therefore, we'll just peek to see if this was the referral to the
        // file. And in that case we'll delete it
        const storageRef = ref(storage, programFile.src);

        if (storageRef) {
          await safeDeleteObject(storageRef);
        }
      }
    } else if (programFile.originalFilename) {
      // new files with transcoding
      const storagePrefix = `programs/${programFile.program}`;

      const {
        downloadFilename,
        originalFilename,
        mediaFolder,
      } = programFile;

      const deletePromises = [];

      const allProgramFiles = await programFileDb.findFileByField('originalFilename', programFile.originalFilename, 2);

      if (allProgramFiles.length <= 1) {
        // When programs with demo videos are duplicated we don't physically duplicate
        // the file. Therefore, we'll just peek to see if this was the referral to the
        // file. And in that case we'll delete it
        if (mediaFolder) {
          deletePromises.push(callFirebaseFunction('deleteStorageDirectory', {
            prefix: `${storagePrefix}/${mediaFolder}/`,
          }));
        }

        if (downloadFilename && originalFilename !== downloadFilename) {
          const downloadFileRef = ref(storage, `${storagePrefix}/${downloadFilename}`);
          deletePromises.push(safeDeleteObject(downloadFileRef));
        }
      }

      await Promise.all(deletePromises);
    }

    if (programFile.id) {
      return programFileDb.delete(programFile.id);
    }

    return null;
  }, // deleteProgramFile()

  /**
   * Delete all files from a program
   */
  async deleteProgramFiles({ program }) {
    // Check if others are using the same file
    const programFileDb = new ProgramFilesDB(program.id);
    const programFiles = await programFileDb.readAll();
    const deleteActions = programFiles.map((programFile) => this.deleteProgramFile({ program, programFile }));

    const isProgramDuplicated = !isEmpty(program.originalProgramId)
      || !isEmpty(program.packageInheritedId);

    if (program.demoVideo && !isProgramDuplicated) {
      const demoVideoRef = ref(storage, program.demoVideo);

      if (demoVideoRef) {
        deleteActions.push(deleteObject(demoVideoRef));
      }
    }

    if (program.coverphoto && !isProgramDuplicated) {
      let coverPhotoRef;
      try {
        coverPhotoRef = ref(storage, program.coverphoto);
      } catch (err) {
        console.error(err);
      }
      if (coverPhotoRef) {
        deleteActions.push(deleteObject(coverPhotoRef));
      }
    }

    return Promise.all(deleteActions);
  }, // deleteProgramFiles()

  setCurrentFolderPath(folderPath = []) {
    this.currentFolderPath = [...folderPath];

    if (isEmpty(this.currentFolderPath)) {
      return sessionStorage.removeItem('currentFolderPath');
    }

    sessionStorage.setItem('currentFolderPath', JSON.stringify(this.currentFolderPath));
  }, // setCurrentFolderPath()

  setProgramTarget({ program, programTargets } = []) {
    if (!program || !programTargets) {
      return;
    }

    const programIndex = findIndex(this.programs, { id: program.id });
    if (programIndex !== -1) {
      this.setProgramTargetMutation({ programIndex, programTargets });
      return;
    }
    const groupProgramIndex = findIndex(this.cachedGroupPrograms[program.client], { id: program.id });
    if (groupProgramIndex === -1) {
      return;
    }
    this.setCachedGroupProgramTarget({ clientId: program.client, groupProgramIndex, programTargets });
  }, // setProgramTarget(),

  getSavedFolderPath() {
    const folderPath = sessionStorage.getItem('currentFolderPath');

    if (folderPath) {
      this.setCurrentFolderPath(JSON.parse(folderPath));
    }
  }, // getSavedFolderPath()

  logout() {
    this.unbindPrograms();
    this.unbindGroupPrograms();
    this.unbindProgramTemplates();
    this.setPrograms([]);
    this.setDeletedPrograms([]);
  }, // logout()

  /**
   * todo: make it possible to provide already fetched session programs to skip loading them
   *
   * @param {string} sessionId
   *
   * @return {Promise<void>}
   */
  async loadDeletedSessionPrograms(sessionId) {
    const sessionProgramsDb = new SessionProgramsDB(sessionId);
    const sessionPrograms = await sessionProgramsDb.readAll();

    if (isEmpty(sessionPrograms)) {
      return;
    }

    const originalProgramIds = sessionPrograms.map((item) => item?.program);

    const programsDb = new ProgramsDB();

    let deletedPrograms;

    // workaround due to firestore 'in' operator limits
    if (originalProgramIds.length > 9) {
      const chunkedProgramsIds = chunk(originalProgramIds, 9);
      const gettingProgramsActions = chunkedProgramsIds.map((programsIds) => programsDb.readAll([
        [documentId(), 'in', programsIds],
        ['isDeleted', '==', true],
      ]));
      const result = await Promise.all(gettingProgramsActions);
      deletedPrograms = flatten(result);
    } else {
      deletedPrograms = await programsDb.readAll([
        [documentId(), 'in', originalProgramIds],
        ['isDeleted', '==', true],
      ]);
    }

    this.setDeletedPrograms(deletedPrograms);
  }, // loadDeletedSessionPrograms()

  updateProgramById({ id, programUpdates }) {
    if (!id || !programUpdates) {
      return;
    }
    const programIndex = findIndex(this.programs, { id });
    if (programIndex === -1) {
      return;
    }

    this.programs[programIndex] = {
      ...this.programs[programIndex],
      ...programUpdates,
    };
  }, // updateProgramById(),

};
